import React, { useCallback, useEffect, useState } from "react";
import { motion } from "framer-motion";
import { MessageSquare, FileText, Clock3, Smartphone, Activity, Check, X, Plus, Trash2, Power, Pencil } from "lucide-react";
import { toast } from "sonner";
import { useLang } from "@/context/LanguageContext";
import devApi from "../devApi";
import { Skeleton, spring } from "../bits";
import { DevScreen, DevCard, DevList, Tgl, Field, DevInput, DevTextarea, SaveBtn } from "./bits";

// ---------- SMS ----------
export const DevSms = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [settings, setSettings] = useState(null);
  const [balance, setBalance] = useState(null);
  const [testPhone, setTestPhone] = useState("");

  const load = useCallback(() => {
    devApi.get("/admin/sms/settings").then((r) => setSettings(r.data)).catch(() => toast.error(m.error_generic));
    devApi.get("/admin/sms/balance").then((r) => setBalance(r.data.balance)).catch(() => setBalance(null));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const toggle = async (v) => {
    try {
      await devApi.post("/admin/sms/toggle", { enabled: v });
      setSettings((s) => ({ ...s, enabled: v }));
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const sendTest = async () => {
    try {
      await devApi.post("/admin/sms/test", { phone: testPhone.trim() });
      toast.success(m.dev_saved);
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  return (
    <DevScreen title={m.sec_sms} onBack={onBack} testid="dev-screen-sms">
      {settings === null ? <Skeleton className="h-24" /> : (
        <DevList>
          <DevCard>
            <div className="flex items-center justify-between">
              <p className="font-bold text-sm text-brand-ink flex items-center gap-2"><MessageSquare className="h-4 w-4 text-brand" /> SMS</p>
              <Tgl on={!!settings.enabled} onChange={toggle} testid="dev-sms-toggle" />
            </div>
          </DevCard>
          <DevCard>
            <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/40">{m.dev_balance}</p>
            <p className="font-heading text-2xl font-bold text-brand mt-1" data-testid="dev-sms-balance">{balance === null ? "—" : balance}</p>
          </DevCard>
          <DevCard>
            <Field label={m.dev_test_sms}>
              <div className="flex gap-2">
                <DevInput value={testPhone} onChange={(e) => setTestPhone(e.target.value)} inputMode="tel" data-testid="dev-sms-test-phone" />
                <SaveBtn onClick={sendTest} label={m.dev_test_send} testid="dev-sms-test-send" />
              </div>
            </Field>
          </DevCard>
        </DevList>
      )}
    </DevScreen>
  );
};

// ---------- Templates ----------
export const DevTemplates = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [tpls, setTpls] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    devApi.get("/admin/templates").then((r) => setTpls(r.data.templates)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);

  const save = async () => {
    setBusy(true);
    try {
      await devApi.put("/admin/templates", { templates: tpls });
      toast.success(m.dev_saved);
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
    finally { setBusy(false); }
  };

  return (
    <DevScreen title={m.sec_templates} onBack={onBack} testid="dev-screen-templates">
      {tpls === null ? <Skeleton className="h-40" /> : (
        <>
          <DevList>
            {Object.entries(tpls).map(([key, val]) => (
              <DevCard key={key}>
                <p className="text-[11px] font-bold uppercase tracking-wide text-brand-ink/40 mb-1.5 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5 text-brand" /> {key}</p>
                <DevTextarea value={val} onChange={(e) => setTpls({ ...tpls, [key]: e.target.value })} data-testid={`dev-tpl-${key}`} />
              </DevCard>
            ))}
          </DevList>
          <div className="mt-4"><SaveBtn onClick={save} busy={busy} label={m.dev_save} testid="dev-tpl-save" /></div>
        </>
      )}
    </DevScreen>
  );
};

// ---------- Store settings (plan prices + opening hours) ----------
const DAY_KEYS = ["0", "1", "2", "3", "4", "5", "6"];
const toMin = (s) => { const [h, mm] = s.split(":").map(Number); return h * 60 + (mm || 0); };
const toHHMM = (mins) => `${String(Math.floor(mins / 60)).padStart(2, "0")}:${String(mins % 60).padStart(2, "0")}`;

export const DevSettings = ({ onBack }) => {
  const { t, lang } = useLang();
  const m = t.mapp;
  const dayNames = lang === "el" ? ["Δευτέρα", "Τρίτη", "Τετάρτη", "Πέμπτη", "Παρασκευή", "Σάββατο", "Κυριακή"] : ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"];
  const [prices, setPrices] = useState(null);
  const [days, setDays] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    devApi.get("/store/plan-prices").then((r) => setPrices(r.data)).catch(() => setPrices({ small: 10, medium: 14, large: 20, giant: 24 }));
    devApi.get("/admin/opening-hours").then((r) => setDays(r.data.days)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);

  const savePrices = async () => {
    setBusy(true);
    try {
      const clean = {};
      ["small", "medium", "large", "giant"].forEach((k) => { clean[k] = Number(prices[k]) || 0; });
      await devApi.put("/admin/plan-prices", { prices: clean });
      toast.success(m.dev_saved);
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
    finally { setBusy(false); }
  };

  const saveHours = async () => {
    setBusy(true);
    try {
      await devApi.put("/admin/opening-hours", { days });
      toast.success(m.dev_saved);
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
    finally { setBusy(false); }
  };

  const setWindow = (dk, idx, part, val) => {
    const next = { ...days };
    const wins = (next[dk] || []).map((w) => [...w]);
    wins[idx][part] = toMin(val);
    next[dk] = wins;
    setDays(next);
  };

  const toggleClosed = (dk, closed) => {
    const next = { ...days };
    next[dk] = closed ? [] : [[9 * 60, 21 * 60]];
    setDays(next);
  };

  return (
    <DevScreen title={m.sec_settings} onBack={onBack} testid="dev-screen-settings">
      <DevList>
        <DevCard>
          <p className="font-bold text-sm text-brand-ink mb-3">{m.dev_plan_prices}</p>
          {prices === null ? <Skeleton className="h-20" /> : (
            <>
              <div className="grid grid-cols-2 gap-3">
                {["small", "medium", "large", "giant"].map((k) => (
                  <Field key={k} label={m[`size_${k}`]}>
                    <DevInput type="number" min="0" value={prices[k]} onChange={(e) => setPrices({ ...prices, [k]: e.target.value })} data-testid={`dev-price-${k}`} />
                  </Field>
                ))}
              </div>
              <div className="mt-3"><SaveBtn onClick={savePrices} busy={busy} label={m.dev_save} testid="dev-prices-save" /></div>
            </>
          )}
        </DevCard>

        <DevCard>
          <p className="font-bold text-sm text-brand-ink mb-3 flex items-center gap-2"><Clock3 className="h-4 w-4 text-brand" /> {m.dev_hours}</p>
          {days === null ? <Skeleton className="h-32" /> : (
            <>
              <div className="space-y-2.5">
                {DAY_KEYS.map((dk, i) => {
                  const wins = days[dk] || [];
                  const closed = wins.length === 0;
                  return (
                    <div key={dk} className="flex items-center gap-2" data-testid={`dev-hours-${dk}`}>
                      <p className="w-20 text-[11px] font-bold text-brand-ink shrink-0">{dayNames[i]}</p>
                      {closed ? (
                        <p className="text-xs text-brand-ink/35 italic flex-1">{m.dev_closed}</p>
                      ) : (
                        <div className="flex items-center gap-1 flex-1 min-w-0">
                          <input type="time" value={toHHMM(wins[0][0])} onChange={(e) => setWindow(dk, 0, 0, e.target.value)} className="h-9 min-w-0 flex-1 rounded-lg border border-brand/20 px-1 text-[11px]" data-testid={`dev-hours-${dk}-from`} />
                          <span className="text-brand-ink/40 text-xs shrink-0">–</span>
                          <input type="time" value={toHHMM(wins[0][1])} onChange={(e) => setWindow(dk, 0, 1, e.target.value)} className="h-9 min-w-0 flex-1 rounded-lg border border-brand/20 px-1 text-[11px]" data-testid={`dev-hours-${dk}-to`} />
                        </div>
                      )}
                      <Tgl on={!closed} onChange={(v) => toggleClosed(dk, !v)} testid={`dev-hours-${dk}-open`} />
                    </div>
                  );
                })}
              </div>
              <div className="mt-3"><SaveBtn onClick={saveHours} busy={busy} label={m.dev_save} testid="dev-hours-save" /></div>
            </>
          )}
        </DevCard>
      </DevList>
    </DevScreen>
  );
};

// ---------- App users ----------
const PERM_KEYS = ["schedule", "bookings", "customers", "orders", "messages", "notifications"];
const emptyUser = { id: null, name: "", code: "", badge: "store", perms: { schedule: true, bookings: true, customers: true, orders: true, messages: true, notifications: true } };

export const DevAppUsers = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const d = t.admin.dev;
  const [users, setUsers] = useState(null);
  const [form, setForm] = useState(null);

  const load = useCallback(() => {
    devApi.get("/admin/app-users").then((r) => setUsers(r.data)).catch(() => toast.error(m.error_generic));
  }, [m.error_generic]);
  useEffect(load, [load]);

  const save = async () => {
    if (!form.name.trim() || (!form.id && !form.code.trim())) return;
    try {
      const payload = { name: form.name.trim(), badge: form.badge, perms: form.perms, ...(form.code.trim() ? { code: form.code.trim() } : {}) };
      if (form.id) await devApi.put(`/admin/app-users/${form.id}`, payload);
      else await devApi.post("/admin/app-users", payload);
      toast.success(m.dev_saved);
      setForm(null);
      load();
    } catch (err) { toast.error(err.response?.data?.detail || m.error_generic); }
  };

  const toggleActive = async (u) => { await devApi.put(`/admin/app-users/${u.id}`, { active: !u.active }); load(); };
  const del = async (u) => { if (!window.confirm(m.dev_del_confirm)) return; await devApi.delete(`/admin/app-users/${u.id}`); load(); };

  return (
    <DevScreen title={m.sec_appusers} onBack={onBack} testid="dev-screen-appusers">
      {!form && (
        <motion.button whileTap={{ scale: 0.97 }} onClick={() => setForm({ ...emptyUser, perms: { ...emptyUser.perms } })} className="mb-4 w-full h-12 rounded-2xl bg-brand text-brand-cream font-bold flex items-center justify-center gap-2" data-testid="dev-au-add">
          <Plus className="h-5 w-5" /> {d.au_add}
        </motion.button>
      )}
      {form && (
        <DevCard className="mb-4">
          <div className="space-y-3">
            <Field label={d.au_name}><DevInput value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="dev-au-name" /></Field>
            <Field label={d.au_code}><DevInput value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value })} data-testid="dev-au-code" /></Field>
            <Field label={d.au_badge}>
              <div className="grid grid-cols-2 gap-2">
                {["store", "management"].map((b) => (
                  <button key={b} type="button" onClick={() => setForm({ ...form, badge: b })} className={`h-10 rounded-xl border font-bold text-sm ${form.badge === b ? "border-brand bg-brand text-brand-cream" : "border-brand/15 bg-white text-brand-ink/60"}`} data-testid={`dev-au-badge-${b}`}>
                    {b === "store" ? d.badge_store : d.badge_management}
                  </button>
                ))}
              </div>
            </Field>
            {form.badge !== "management" && (
              <div className="space-y-2">
                {PERM_KEYS.map((k) => (
                  <div key={k} className="flex items-center justify-between gap-2" data-testid={`dev-au-perm-${k}`}>
                    <p className="text-xs font-semibold text-brand-ink">{d[`perm_${k}`]}</p>
                    <Tgl on={!!form.perms[k]} onChange={(v) => setForm({ ...form, perms: { ...form.perms, [k]: v } })} testid={`dev-au-perm-tgl-${k}`} />
                  </div>
                ))}
              </div>
            )}
            <div className="flex items-center gap-3">
              <SaveBtn onClick={save} label={d.au_save} testid="dev-au-save" />
              <button type="button" onClick={() => setForm(null)} className="text-sm font-bold text-brand-ink/50 px-2" data-testid="dev-au-cancel">✕</button>
            </div>
          </div>
        </DevCard>
      )}
      {users === null ? <Skeleton className="h-20" /> : users.length === 0 ? (
        <p className="text-sm text-brand-ink/40 italic py-8 text-center">{m.dev_empty}</p>
      ) : (
        <DevList>
          {users.map((u) => (
            <DevCard key={u.id} className={u.active ? "" : "opacity-60"}>
              <div className="flex items-center gap-3" data-testid={`dev-au-row-${u.id}`}>
                <div className="grid place-items-center h-10 w-10 rounded-xl bg-brand-soft text-brand shrink-0"><Smartphone className="h-5 w-5" /></div>
                <div className="min-w-0 flex-1">
                  <p className="font-bold text-sm text-brand-ink flex items-center gap-2 flex-wrap">
                    {u.name}
                    <span className={`text-[9px] font-bold uppercase px-2 py-0.5 rounded-full ${u.badge === "management" ? "bg-brand text-brand-cream" : "bg-stone-200 text-stone-600"}`}>
                      {u.badge === "management" ? d.badge_management : d.badge_store}
                    </span>
                  </p>
                  <p className="text-[10px] text-brand-ink/50 mt-0.5 truncate">
                    {u.badge === "management" ? d.au_badge_hint : PERM_KEYS.filter((k) => u.perms?.[k]).map((k) => d[`perm_${k}`]).join(" · ") || "—"}
                  </p>
                </div>
                <div className="flex gap-1 shrink-0">
                  <button type="button" onClick={() => toggleActive(u)} className="grid place-items-center h-8 w-8 rounded-full text-brand hover:bg-brand-soft/60" data-testid={`dev-au-toggle-${u.id}`} aria-label="active"><Power className="h-4 w-4" /></button>
                  <button type="button" onClick={() => setForm({ id: u.id, name: u.name, code: "", badge: u.badge || "store", perms: { ...emptyUser.perms, ...(u.perms || {}) } })} className="grid place-items-center h-8 w-8 rounded-full text-brand hover:bg-brand-soft/60" data-testid={`dev-au-edit-${u.id}`} aria-label="edit"><Pencil className="h-4 w-4" /></button>
                  <button type="button" onClick={() => del(u)} className="grid place-items-center h-8 w-8 rounded-full text-rose-400 hover:bg-rose-50" data-testid={`dev-au-del-${u.id}`} aria-label="delete"><Trash2 className="h-4 w-4" /></button>
                </div>
              </div>
            </DevCard>
          ))}
        </DevList>
      )}
    </DevScreen>
  );
};

// ---------- System ----------
export const DevSystem = ({ onBack }) => {
  const { t } = useLang();
  const m = t.mapp;
  const [checks, setChecks] = useState(null);

  useEffect(() => {
    devApi.get("/admin/health").then((r) => setChecks(r.data.checks || r.data)).catch(() => setChecks({}));
  }, []);

  const entries = checks ? Object.entries(checks) : [];
  const allOk = entries.every(([, v]) => v === true || v === "ok" || v?.ok === true || v?.status === "ok");

  return (
    <DevScreen title={m.sec_system} onBack={onBack} testid="dev-screen-system">
      {checks === null ? <Skeleton className="h-32" /> : (
        <DevList>
          <DevCard>
            <p className={`font-bold text-sm flex items-center gap-2 ${allOk ? "text-emerald-600" : "text-amber-600"}`} data-testid="dev-health-summary">
              <Activity className="h-4 w-4" /> {allOk ? m.dev_health_ok : m.dev_health_issue}
            </p>
          </DevCard>
          {entries.map(([k, v]) => {
            const ok = v === true || v === "ok" || v?.ok === true || v?.status === "ok";
            return (
              <DevCard key={k}>
                <p className="flex items-center gap-2 text-sm font-semibold text-brand-ink" data-testid={`dev-health-${k}`}>
                  {ok ? <Check className="h-4 w-4 text-emerald-500" /> : <X className="h-4 w-4 text-rose-500" />} {k}
                </p>
              </DevCard>
            );
          })}
        </DevList>
      )}
    </DevScreen>
  );
};
